import { ApolloError, useApolloClient, useQuery } from "@apollo/client";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useCanAccess, useNotify, useTranslate } from "react-admin";

import { TELEPHONY_RESOURCE } from "../../constants/resources";
import {
  AddPlacetelCallNoteDocument,
  type AddPlacetelCallNoteMutation,
  type AddPlacetelCallNoteMutationVariables,
  type CrmSearchHit,
  IssuePlacetelPhoneAccessTokenDocument,
  type IssuePlacetelPhoneAccessTokenMutation,
  type IssuePlacetelPhoneAccessTokenMutationVariables,
  PlacetelPhoneAccountDocument,
  RecordPlacetelCallEventDocument,
  type RecordPlacetelCallEventMutation,
  type RecordPlacetelCallEventMutationVariables,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { setShutdownHook } from "../../lib/telephony/callingSession";
import {
  type ActiveCall,
  type AnswerResult,
  CALL_NUMBER_INVALID_ERROR,
  CallNumberInvalidError,
  createPhoneEngine,
  type DialRecord,
  type DialResult,
  type EndedCall,
  type PhoneEngine,
  type PhoneStatus,
} from "../../lib/telephony/phoneEngine";
import { armRingtone, startRingtone, stopRingtone } from "../../lib/telephony/ringtone";
import { loadWebexCalling } from "../../lib/telephony/webexCalling";
import { type CallerMatches, NO_CALLER_MATCHES, useCallerMatches } from "./useCallerMatches";

const INCOMING_CALL_NOTIFICATION_TAG = "phone-incoming-call";
const CRM_TIMELINE_QUERY_NAME = "CustomerServiceCrmTimeline";
const PHONE_ACCOUNT_POLL_INTERVAL_MS = 30_000;

/** The agent's note for a call; it stays until it is saved or discarded, also after the call ended. */
export type CallNote = {
  callId: string;
  number: string | null;
  direction: ActiveCall["direction"];
  /** Whether the call is linked to a CRM record; a note of an unlinked call appears nowhere in the CRM. */
  linked: boolean;
  text: string;
  ended: boolean;
  saving: boolean;
};

// A call that got its id drops the earlier calls' notes the agent never wrote in.
const withNoteForCall = (notes: CallNote[], call: ActiveCall & { callId: string }): CallNote[] => {
  if (notes.some(note => note.callId === call.callId)) return notes;
  const kept = notes.filter(note => !note.ended || note.text !== "");
  return [
    ...kept,
    {
      callId: call.callId,
      number: call.number,
      direction: call.direction,
      linked: call.record !== null,
      text: "",
      ended: false,
      saving: false,
    },
  ];
};

const withNoteEnded = (notes: CallNote[], call: EndedCall): CallNote[] =>
  notes.map(note =>
    note.callId === call.callId ? { ...note, number: call.number, linked: call.linked, ended: true } : note
  );

// The call and the notes live in two contexts: typing a note changes only the notes, so the
// components that dial, show the status or ring do not render on each keystroke.
type PhoneContextValue = {
  /** Null while no phone is running, e.g. without telephony access or before the account is connected. */
  status: PhoneStatus | null;
  /**
   * The status to show. The account refetch removes the phone once it is not connected, so that moment
   * and the time before the phone has started both read as starting.
   */
  displayStatus: PhoneStatus["kind"];
  retry: () => void;
  dial: (number: string, record?: DialRecord) => Promise<DialResult>;
  answer: () => Promise<AnswerResult>;
  decline: () => void;
  hangUp: () => void;
  setMuted: (muted: boolean) => void;
  /** Links the running call to a CRM record, replacing the one it was dialled with. */
  linkRecord: (record: DialRecord) => void;
  activeCall: ActiveCall | null;
  /** The CRM records that fit the caller of an incoming call this tab answered. */
  callerMatches: CallerMatches;
  chooseCallerMatch: (hit: CrmSearchHit) => void;
  retryCallerLookup: () => void;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
};

type PhoneNotesContextValue = {
  /** The notes of the running call and of ended calls that were not saved or discarded yet. */
  notes: CallNote[];
  setNoteText: (callId: string, text: string) => void;
  saveNote: (callId: string) => Promise<void>;
  discardNote: (callId: string) => void;
};

const PhoneContext = createContext<PhoneContextValue>({
  status: null,
  displayStatus: "starting",
  retry: () => undefined,
  dial: async () => ({ ok: false, reason: "notReady" }),
  answer: async () => ({ ok: false, reason: "noCall" }),
  decline: () => undefined,
  hangUp: () => undefined,
  setMuted: () => undefined,
  linkRecord: () => undefined,
  activeCall: null,
  callerMatches: NO_CALLER_MATCHES,
  chooseCallerMatch: () => undefined,
  retryCallerLookup: () => undefined,
  panelOpen: false,
  openPanel: () => undefined,
  closePanel: () => undefined,
});

const PhoneNotesContext = createContext<PhoneNotesContextValue>({
  notes: [],
  setNoteText: () => undefined,
  saveNote: async () => undefined,
  discardNote: () => undefined,
});

/** The call, the line's status and the panel; it does not change while a note is typed. */
export const usePhone = (): PhoneContextValue => useContext(PhoneContext);

/** The call notes. */
export const usePhoneNotes = (): PhoneNotesContextValue => useContext(PhoneNotesContext);

/** Starts the agent's portal phone once their phone account is connected and keeps it registered. */
export const PhoneProvider = ({ children }: { children: ReactNode }) => {
  const client = useApolloClient();
  const translate = useTranslate();
  const notify = useNotify();
  const { canAccess } = useCanAccess({ action: "list", resource: TELEPHONY_RESOURCE });
  const { data } = useQuery(PlacetelPhoneAccountDocument, {
    skip: !canAccess,
    fetchPolicy: "cache-and-network",
    pollInterval: canAccess ? PHONE_ACCOUNT_POLL_INTERVAL_MS : 0,
  });
  const [status, setStatus] = useState<PhoneStatus | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [notes, setNotes] = useState<CallNote[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const engineRef = useRef<PhoneEngine | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const notificationPermissionAsked = useRef(false);
  // saveNote reads the notes through this ref, so it stays the same function while a note is typed.
  const notesRef = useRef(notes);
  // The phone engine lives longer than one render: it tells the agent through the latest notify.
  const notifyRef = useRef(notify);

  const account = data?.placetelPhoneAccount;
  const enabled = canAccess && Boolean(account?.available && account.connected);
  useEffect(() => {
    if (!enabled) return undefined;

    const engine = createPhoneEngine({
      loadCalling: loadWebexCalling,
      issueToken: async ({ renew }) => {
        const result = await client.mutate<
          IssuePlacetelPhoneAccessTokenMutation,
          IssuePlacetelPhoneAccessTokenMutationVariables
        >({
          mutation: IssuePlacetelPhoneAccessTokenDocument,
          variables: { renew },
          fetchPolicy: "no-cache",
        });
        const issued = result.data?.issuePlacetelPhoneAccessToken;
        if (!issued) throw new Error("Phone access request returned no data");
        return {
          accessToken: issued.accessToken ?? null,
          expiresAt: issued.expiresAt ?? null,
          errorCode: issued.errorCode ?? null,
        };
      },
      recordEvent: async (input, options) => {
        let result;
        try {
          result = await client.mutate<RecordPlacetelCallEventMutation, RecordPlacetelCallEventMutationVariables>({
            mutation: RecordPlacetelCallEventDocument,
            variables: { input },
            fetchPolicy: "no-cache",
            // The Apollo HTTP link passes fetchOptions on to fetch; keepalive lets the request outlive the page.
            ...(options?.keepalive ? { context: { fetchOptions: { keepalive: true } } } : {}),
          });
        } catch (error) {
          // The engine tells a refused number from other failures by this code.
          const code = error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined;
          if (code === CALL_NUMBER_INVALID_ERROR) throw new CallNumberInvalidError();
          throw error;
        }
        const recorded = result.data?.recordPlacetelCallEvent;
        if (!recorded) throw new Error("Call event request returned no data");
        return { id: recorded.id, remoteNumber: recorded.remoteNumber ?? null };
      },
      onCall: next => {
        setActiveCall(next);
        // An outgoing call has a note from its first id; an incoming one only once it connected,
        // so rings nobody answered leave no note card behind.
        if (next?.callId && (next.direction === "outbound" || next.phase === "connected")) {
          const callId = next.callId;
          setNotes(current => withNoteForCall(current, { ...next, callId }));
        }
      },
      onCallEnded: ended => setNotes(current => withNoteEnded(current, ended)),
      // The panel reports a failed answer the same way; here the answer had already gone through.
      onConnectFailed: () => notifyRef.current("telephony.call.failed", { type: "error" }),
      onReloadRequired: () => window.location.reload(),
      onRemoteMedia: track => {
        const audio = audioRef.current;
        if (audio) audio.srcObject = track ? new MediaStream([track]) : null;
      },
      onStatus: next => {
        setStatus(next);
        if (next.kind === "notConnected") {
          // The header then offers the phone activation again.
          client.refetchQueries({ include: [PlacetelPhoneAccountDocument] }).catch(error => {
            captureException(error, "telephony/phone: refresh account");
          });
        }
      },
    });
    engineRef.current = engine;
    // The logout ends a running call through the engine before it takes the agent's token away; the page
    // reloads right after, so the end is sent with keepalive.
    setShutdownHook(() => engine.shutdown({ keepalive: true }));
    const shutdownOnPageHide = () => {
      void engine.shutdown({ unloading: true });
    };
    // A page restored from the back/forward cache was shut down by pagehide, and so was its SDK: it reloads.
    const reloadOnPageRestore = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload();
    };
    const disarmRingtone = armRingtone();
    window.addEventListener("pagehide", shutdownOnPageHide);
    window.addEventListener("pageshow", reloadOnPageRestore);
    void engine.start();

    return () => {
      disarmRingtone();
      window.removeEventListener("pagehide", shutdownOnPageHide);
      window.removeEventListener("pageshow", reloadOnPageRestore);
      engineRef.current = null;
      setShutdownHook(null);
      void engine.shutdown();
      setStatus(null);
      setActiveCall(null);
      setPanelOpen(false);
    };
  }, [client, enabled]);

  const retry = useCallback(() => {
    void engineRef.current?.retry();
  }, []);
  const dial = useCallback(
    async (number: string, record?: DialRecord): Promise<DialResult> =>
      engineRef.current ? engineRef.current.dial(number, record) : { ok: false, reason: "notReady" },
    []
  );
  const answer = useCallback(
    async (): Promise<AnswerResult> =>
      engineRef.current ? engineRef.current.answer() : { ok: false, reason: "noCall" },
    []
  );
  const decline = useCallback(() => engineRef.current?.decline(), []);
  const hangUp = useCallback(() => engineRef.current?.hangUp(), []);
  const setMuted = useCallback((muted: boolean) => engineRef.current?.setMuted(muted), []);
  const linkRecord = useCallback((record: DialRecord) => engineRef.current?.linkRecord(record), []);
  const { callerMatches, chooseCallerMatch, retryCallerLookup } = useCallerMatches(activeCall, linkRecord);
  const displayStatus: PhoneStatus["kind"] = !status || status.kind === "notConnected" ? "starting" : status.kind;

  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);
  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  const updateNote = useCallback((callId: string, changes: Partial<CallNote>) => {
    setNotes(current => current.map(note => (note.callId === callId ? { ...note, ...changes } : note)));
  }, []);
  const setNoteText = useCallback((callId: string, text: string) => updateNote(callId, { text }), [updateNote]);
  const discardNote = useCallback((callId: string) => {
    setNotes(current => current.filter(note => note.callId !== callId));
  }, []);
  const saveNote = useCallback(
    async (callId: string): Promise<void> => {
      const note = notesRef.current.find(candidate => candidate.callId === callId);
      const body = note?.text.trim() ?? "";
      if (!note || note.saving || body === "") return;
      updateNote(callId, { saving: true });
      try {
        await client.mutate<AddPlacetelCallNoteMutation, AddPlacetelCallNoteMutationVariables>({
          mutation: AddPlacetelCallNoteDocument,
          variables: { input: { callId, body } },
          fetchPolicy: "no-cache",
          refetchQueries: [CRM_TIMELINE_QUERY_NAME],
        });
      } catch (error) {
        // The note's text and the call's number stay out of the report.
        captureException(error, "telephony/phone: call note");
        notify("telephony.note.failed", { type: "error" });
        updateNote(callId, { saving: false });
        return;
      }
      notify("telephony.note.saved", { type: "success" });
      // A note of a running call stays for the rest of the call; an ended one is done.
      setNotes(current =>
        current.flatMap(candidate => {
          if (candidate.callId !== callId) return [candidate];
          return candidate.ended ? [] : [{ ...candidate, text: "", saving: false }];
        })
      );
    },
    [client, notify, updateNote]
  );
  const openPanel = useCallback(() => {
    setPanelOpen(true);
    // Asked once, when the agent first opens the phone: a hidden tab can then show an incoming call.
    if (notificationPermissionAsked.current || typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    notificationPermissionAsked.current = true;
    Promise.resolve(Notification.requestPermission()).catch(error => {
      captureException(error, "telephony/phone: notification permission");
    });
  }, []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const ringing = activeCall?.direction === "inbound" && activeCall.phase === "ringing";
  const callerNumber = activeCall?.number ?? null;
  const titlePrefix = translate("telephony.incoming.titlePrefix");
  const notificationTitle = translate("telephony.incoming.notificationTitle");
  const unknownNumber = translate("telephony.incoming.unknownNumber");

  useEffect(() => {
    if (ringing) setPanelOpen(true);
  }, [ringing]);

  useEffect(() => {
    if (!ringing) return undefined;
    startRingtone();
    return stopRingtone;
  }, [ringing]);

  useEffect(() => {
    if (!ringing) return undefined;
    document.title = `${titlePrefix}${document.title}`;
    return () => {
      if (document.title.startsWith(titlePrefix)) document.title = document.title.slice(titlePrefix.length);
    };
  }, [ringing, titlePrefix]);

  useEffect(() => {
    if (!ringing || document.visibilityState !== "hidden") return undefined;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return undefined;
    // Some browsers (Chrome on Android) refuse the constructor; the ring goes on without a notification.
    let notification: Notification;
    try {
      notification = new Notification(notificationTitle, {
        body: callerNumber ?? unknownNumber,
        tag: INCOMING_CALL_NOTIFICATION_TAG,
      });
      notification.onclick = () => {
        window.focus();
        setPanelOpen(true);
        notification.close();
      };
    } catch (error) {
      captureException(error, "telephony/phone: notification");
      return undefined;
    }
    return () => notification.close();
  }, [ringing, callerNumber, notificationTitle, unknownNumber]);

  const value = useMemo(
    () => ({
      status,
      displayStatus,
      retry,
      dial,
      answer,
      decline,
      hangUp,
      setMuted,
      linkRecord,
      activeCall,
      callerMatches,
      chooseCallerMatch,
      retryCallerLookup,
      panelOpen,
      openPanel,
      closePanel,
    }),
    [
      status,
      displayStatus,
      retry,
      dial,
      answer,
      decline,
      hangUp,
      setMuted,
      linkRecord,
      activeCall,
      callerMatches,
      chooseCallerMatch,
      retryCallerLookup,
      panelOpen,
      openPanel,
      closePanel,
    ]
  );
  const notesValue = useMemo(
    () => ({ notes, setNoteText, saveNote, discardNote }),
    [notes, setNoteText, saveNote, discardNote]
  );

  return (
    <PhoneContext.Provider value={value}>
      <PhoneNotesContext.Provider value={notesValue}>
        {children}
        <audio ref={audioRef} autoPlay hidden data-testid="phone-remote-audio" />
      </PhoneNotesContext.Provider>
    </PhoneContext.Provider>
  );
};
